import { NumberUtils } from '@utils/helpers/index';

interface IForeignObjectFixedSizeCalculationData {
	foreignObjectElement: SVGForeignObjectElement;
	svg: SVGSVGElement;
	svgScaleRatios?: {
		widthScale: number;
		heightScale: number;
	};
	width: string;
	height: string;
	x: string;
	y: string;
}

/**
 * Calculates the scale of an SVGElement as the ratio between its actual size
 * as set via CSS and its viewBox size.
 */
function calculateSVGScale( svg: SVGSVGElement ): { widthScale: number; heightScale: number; } {
	// viewBox dimensions
	const w1 = svg.viewBox.animVal.width;
	const h1 = svg.viewBox.animVal.height;
	let widthScale;
	let heightScale;

	if ( !w1 && !h1 ) { // No viewBox
		widthScale = 1;
		heightScale = 1;
	}
	else {
		// dimensions of the SVG's viewport as set via CSS (cf. https://svgwg.org/svg2-draft/coords.html#Introduction)
		const css = getComputedStyle( svg );
		const w2 = parseFloat( css.width );
		const h2 = parseFloat( css.height );
		const par = svg.preserveAspectRatio.animVal;

		if ( par.align === SVGPreserveAspectRatio.SVG_PRESERVEASPECTRATIO_NONE ) {
			widthScale = w2 / w1;
			heightScale = h2 / h1;
		} else {
			// "default branch" (aspect ratio is preserved, with "meet" as the default "fit-in" behavior)
			const isMeet = par.meetOrSlice === SVGPreserveAspectRatio.SVG_MEETORSLICE_MEET;
			const isViewportNarrowerOrHigherThanViewBox = w1 / h1 > w2 / h2;
			const preferredRatio = isViewportNarrowerOrHigherThanViewBox !== isMeet
										? h2 / h1
										: w2 / w1;

			widthScale = preferredRatio;
			heightScale = preferredRatio;
		}
	}

	return {
		widthScale,
		heightScale
	};
}

/**
 * Performs scale correction on an `SVGForeignObjectElement`.
 * For background and details on the methodology see https://stackoverflow.com/a/45044646.
 * @param foreignObjectFixedSizeCalculationData Object holding the
 * `SVGForeignObjectElement` for which the "fixed scale" is to be set, as well
 * as size, position and scale values based on which the "fixed scale" is set.
 */
function fixScale( foreignObjectFixedSizeCalculationData: IForeignObjectFixedSizeCalculationData ): void {
	const foreignObjectElement = foreignObjectFixedSizeCalculationData.foreignObjectElement;
	const width = parseInt( foreignObjectFixedSizeCalculationData.width, 10 );
	const height = parseInt( foreignObjectFixedSizeCalculationData.height, 10 );
	const x = foreignObjectFixedSizeCalculationData.x;
	const y = foreignObjectFixedSizeCalculationData.y;

	const widthScale = foreignObjectFixedSizeCalculationData.svgScaleRatios.widthScale;
	const heightScale = foreignObjectFixedSizeCalculationData.svgScaleRatios.heightScale;

	foreignObjectElement.setAttribute( 'width', `${ width * widthScale }` );
	foreignObjectElement.setAttribute( 'height', `${ height * heightScale }` );
	foreignObjectElement.setAttribute( 'transform', `translate(${ x }, ${ y }) scale(${ 1 / widthScale }, ${ 1 / heightScale })` );
}

function fixedSizeForeignObjects( foreignObjectElements: SVGForeignObjectElement[] ): void {
	foreignObjectElements.forEach( fixedSizeForeignObject );
}

function fixedSizeForeignObject( foreignObjectElement: SVGForeignObjectElement ): void {
	const svg = foreignObjectElement.ownerSVGElement;

	const foreignObjectFixedSizeCalculationData: IForeignObjectFixedSizeCalculationData = {
		foreignObjectElement,
		svg,
		svgScaleRatios: calculateSVGScale( svg ),
		width: foreignObjectElement.getAttribute( 'width' ),
		height: foreignObjectElement.getAttribute( 'height' ),
		x: foreignObjectElement.getAttribute( 'x' ),
		y: foreignObjectElement.getAttribute( 'y' )
	};

	foreignObjectElement.removeAttribute( 'x' );
	foreignObjectElement.removeAttribute( 'y' );

	fixScale( foreignObjectFixedSizeCalculationData );
}

/**
 * Converts polar coordinates [radius, angle] to Cartesian coordinates [x, y].
 * @param centerX x coordinate of the center of a circle
 * @param centerY y coordinate of the center of that circle
 * @param radius the radius of that circle
 * @param angleInDegrees angle relative to 0°, which together with the radius
 * describes the position of a point on the circumference of that circle
 * @returns a plain object holding the Cartesian coordinates of the point on the
 * circumference of the circle described by the input values
 *
 * **Note that the function does an angle correction by -90° resulting in 0°
 * being located at "12 o'clock"!**
 *
 */
function polarToCartesian( centerX, centerY, radius, angleInDegrees ): { x: number; y: number } {
	const angleInRadians = ( angleInDegrees - 90 ) * Math.PI / 180;
	return {
		x: centerX + radius * Math.cos( angleInRadians ),
		y: centerY + radius * Math.sin( angleInRadians )
	};
}

/**
 Creates the path definition for an arc.
 @param x position (center of the full circle the arc is a part of)
 @param y position (center of the full circle the arc is a part of)
 @param radius radius
 @param startAngle angle where the arc starts in degrees
 @param endAngle angle where the arc ends in degrees
 @returns path data calculated from the input values
 */
function describeArcPath( x: number, y: number, radius: number, startAngle: number, endAngle: number ): string {
	endAngle = endAngle >= 360 ? 359.99999 : endAngle;

	const start = polarToCartesian( x, y, radius, endAngle );
	const end = polarToCartesian( x, y, radius, startAngle );

	const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

	const round = ( n: number ): number => NumberUtils.roundFloat( n, 5 );

	// for details see https://svgwg.org/svg2-draft/paths.html#PathData
	const pathData = [
		'M', round( start.x ), round( start.y ),
		'A', radius ?? 0, radius ?? 0, 0, largeArcFlag, 0, round( end.x ), round( end.y )
	].join( ' ' );

	return pathData;
}

/**
 * FOR UNIT TESTS ONLY!
 * Do not import anywhere else than in svg-utils.spec.ts file!
 */
export const UT = {
	// "hack" to get this type exported in this "encapsuled" way
	ForeignObjectFixedSizeCalculationData: {} as IForeignObjectFixedSizeCalculationData,
	calculateSVGScale,
	fixScale,
	polarToCartesian
};

export const publicFunctions = {
	describeArcPath,
	fixedSizeForeignObjects,
	fixedSizeForeignObject
}
